import { Component, ElementRef, HostListener, Input, OnDestroy, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService, Language } from '../../services/language.service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button #trigger
      (click)="toggle(); $event.stopPropagation()"
      class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-[var(--dark-orange)] text-[var(--dark-orange)] font-bold text-sm uppercase transition-all hover:bg-[var(--dark-orange)] hover:text-white"
    >
      <span>{{ languageService.current() | uppercase }}</span>
      <svg class="w-3 h-3 transition-transform duration-200" [class.rotate-180]="open()"
        fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>
  `,
  styles: [`:host { display: inline-block; }`]
})
export class LanguageSelectorComponent implements OnDestroy {
  @Input() openUp = false;
  @ViewChild('trigger') trigger!: ElementRef<HTMLButtonElement>;

  open = signal(false);
  private dropdownEl: HTMLElement | null = null;
  private justOpened = false;

  constructor(public languageService: LanguageService) {}

  toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.openDropdown();
    }
  }

  private openDropdown(): void {
    const rect = this.trigger.nativeElement.getBoundingClientRect();
    const dropdownHeight = this.languageService.available.length * 44;

    const top = this.openUp
      ? rect.top - dropdownHeight - 8
      : rect.bottom + 8;

    this.dropdownEl = document.createElement('div');
    this.dropdownEl.style.cssText = `
      position: fixed;
      top: ${top}px;
      left: ${rect.left}px;
      width: ${rect.width}px;
      z-index: 999999;
      background: var(--beige);
      border: 2px solid var(--dark-orange);
      border-radius: 0.75rem;
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      overflow: hidden;
    `;

    this.languageService.available.forEach(lang => {
      const btn = document.createElement('button');
      const isActive = lang.code === this.languageService.current();
      btn.innerHTML = `<span style="font-size:1.1rem; line-height:1;">${lang.flag}</span><span>${lang.code.toUpperCase()}</span>`;
      btn.style.cssText = `
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 10px 16px;
        font-size: 0.875rem;
        font-weight: 700;
        text-transform: uppercase;
        cursor: pointer;
        border: none;
        font-family: inherit;
        transition: background 0.15s, color 0.15s;
        background: ${isActive ? 'var(--dark-orange)' : 'transparent'};
        color: ${isActive ? 'white' : 'var(--dark-orange)'};
      `;
      btn.addEventListener('mouseenter', () => {
        if (!isActive) {
          btn.style.background = '#CBBFB2';
          btn.style.color = 'var(--dark-orange)';
        }
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = isActive ? 'var(--dark-orange)' : 'transparent';
        btn.style.color = isActive ? 'white' : 'var(--dark-orange)';
      });
      btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.languageService.use(lang.code as Language);
        this.close();
      });
      this.dropdownEl!.appendChild(btn);
    });

    document.body.appendChild(this.dropdownEl);
    this.open.set(true);

    this.justOpened = true;
    setTimeout(() => { this.justOpened = false; }, 0);
  }

  private close(): void {
    if (this.dropdownEl) {
      this.dropdownEl.remove();
      this.dropdownEl = null;
    }
    this.open.set(false);
  }

  ngOnDestroy(): void {
    this.close();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.justOpened) return;
    if (this.open()) this.close();
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.open()) this.close();
  }
}
